import type { Column, Table, VisibilityState } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { isNil } from 'es-toolkit';

import type { ColumnVisibilityColumn } from '../types/column-visibility';

export type UseColumnVisibilityOptions<TData> = {
  readonly table: Table<TData>;
  readonly setColumnVisibility: (state: VisibilityState) => void;
  readonly enabled: boolean;
};

export type UseColumnVisibilityReturn = {
  readonly columns: ColumnVisibilityColumn[];
  readonly pendingColumnIds: string[];
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly handleOpenChange: (open: boolean) => void;
  readonly toggleColumn: (columnId: string) => void;
  readonly showAll: () => void;
  readonly hideAll: () => void;
  readonly apply: () => void;
  readonly reset: () => void;
  readonly columnVisibilityEnabled: boolean;
};

function getColumnHeader<TData>(column: Column<TData>): string {
  const label = column.columnDef.meta?.columnVisibility?.label;
  if (!isNil(label)) return label;
  const header = column.columnDef.header;
  if (typeof header === 'string') return header;
  return column.id;
}

export function useColumnVisibility<TData>({
  table,
  setColumnVisibility,
  enabled: columnVisibilityEnabled,
}: UseColumnVisibilityOptions<TData>): UseColumnVisibilityReturn {
  // Decompose the mutable table instance: call getAllLeafColumns() outside of any
  // hooks so the returned array — not the stable table reference — is the reactive
  // dependency. TanStack Table forces a re-render when state changes, so this value
  // is always fresh, and memos/callbacks that depend on it will correctly re-run.
  const allLeafColumns = table.getAllLeafColumns();

  const columns = useMemo((): ColumnVisibilityColumn[] => {
    if (!columnVisibilityEnabled) return [];
    return allLeafColumns
      .filter((col) => col.getCanHide())
      .map((col) => ({
        id: col.id,
        header: getColumnHeader(col),
      }));
  }, [allLeafColumns, columnVisibilityEnabled]);

  const defaultVisibleColumnIds = useMemo(() => {
    return allLeafColumns
      .filter((col) => col.getCanHide() && col.columnDef.meta?.columnVisibility?.defaultVisible !== false)
      .map((col) => col.id);
  }, [allLeafColumns]);

  const getCommittedVisibleIds = useCallback((): string[] => {
    return allLeafColumns.filter((col) => col.getCanHide() && col.getIsVisible()).map((col) => col.id);
  }, [allLeafColumns]);

  const [pendingColumnIds, setPendingColumnIds] = useState<string[]>(() => getCommittedVisibleIds());
  const [open, setOpen] = useState(false);

  const toggleColumn = useCallback((columnId: string) => {
    setPendingColumnIds((prev) => {
      if (prev.includes(columnId)) {
        // Prevent hiding the last visible column — keep at least one selected
        if (prev.length <= 1) return prev;
        return prev.filter((id) => id !== columnId);
      }
      return [...prev, columnId];
    });
  }, []);

  const showAll = useCallback(() => {
    setPendingColumnIds(columns.map((col) => col.id));
  }, [columns]);

  const hideAll = useCallback(() => {
    // Keep the first column selected to honor the prevent-hide-all guarantee
    const first = columns[0];
    if (!first) {
      setPendingColumnIds([]);
      return;
    }
    setPendingColumnIds([first.id]);
  }, [columns]);

  const apply = useCallback(() => {
    const newVisibility: VisibilityState = {};
    for (const col of columns) {
      newVisibility[col.id] = pendingColumnIds.includes(col.id);
    }
    setColumnVisibility(newVisibility);
    setOpen(false);
  }, [columns, pendingColumnIds, setColumnVisibility]);

  const reset = useCallback(() => {
    setPendingColumnIds(defaultVisibleColumnIds);
  }, [defaultVisibleColumnIds]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        setPendingColumnIds(getCommittedVisibleIds());
      }
    },
    [getCommittedVisibleIds],
  );

  return {
    columns,
    pendingColumnIds,
    open,
    setOpen,
    handleOpenChange,
    toggleColumn,
    showAll,
    hideAll,
    apply,
    reset,
    columnVisibilityEnabled,
  };
}
